import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { useAuth } from '@/src/app/context/useAuth';
import { Container, Loading, SimpleButton, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import ExamAssignmentsTable from './components/ExamAssignmentsTable';
import CreateEditExamAssignmentsModal from './components/CreateEditExamAssignmentsModal';
import SyncAttemptsModal from './components/SyncAttemptsModal';
import DeleteExamAssignmentModal from './components/DeleteExamAssignmentModal';
import CompletionStatusReportModal from '../../reports/completionStatusReport/components/CompletionStatusReportModal';
import type { ExamAssignmentBasicDTO } from './models/assignmentDTOs';
import ExamAssignmentFilters from './components/ExamAssignmentFilters';
import { Constants } from '@/src/utils';

const ExamAssignmentsPage: React.FC = () => {
    document.title = 'Assessments';

    const [searchString, setSearchString] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<Array<ExamAssignmentBasicDTO>>([]);

    const [showCreateEditModal, setShowCreateEditModal] = useState(false);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCompletionStatusReportModal, setShowCompletionStatusReportModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view' | null>(null);
    const [selectedExamAssignmentId, setSelectedExamAssignmentId] = useState<number | null>(null);

    const { user } = useAuth();
    const isSuperAdmin = user?.role === Constants.ROLES.SUPER_ADMIN;

    const refreshTableData = useCallback(async () => {
        setError('');
        setLoaded(false);

        const requestUrl = `assessments/examAssignments`;
        try {
            const res = await apiRequest<Array<ExamAssignmentBasicDTO>>('GET', requestUrl);
            setData(res.data);
        } catch {
            setError('Failed to fetch assessment data');
        } finally {
            setLoaded(true);
        }
    }, []);

    const handleModalClose = useCallback(
        (shouldRefresh: boolean, isInTimeout: boolean) => {
            setShowCreateEditModal(false);
            setShowSyncModal(false);
            setShowDeleteModal(false);
            setShowCompletionStatusReportModal(false);
            // Don't allow the modal to be reopened while it is in its post-submit timeout
            if (!isInTimeout) {
                setSelectedExamAssignmentId(null);
                setModalMode(null);
            }

            if (shouldRefresh) {
                refreshTableData();
            }
        },
        [refreshTableData]
    );

    useEffect(() => {
        (async function init() {
            await refreshTableData();
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <h1 className="page-header">Assessments</h1>
            <br />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    gap: 2,
                    flexWrap: 'wrap', // allows wrapping on smaller screens
                }}
            >
                <ExamAssignmentFilters setSearchString={setSearchString} />

                <SimpleButton
                    onClick={() => {
                        setModalMode('create');
                        setSelectedExamAssignmentId(null);
                        setShowCreateEditModal(true);
                    }}
                    variant="contained"
                    color="primary"
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    Add Assessment
                </SimpleButton>
            </Box>

            <Loading visible={!loaded} message="Loading assessments..." />
            <ErrorMessage error={error} />

            {loaded && (
                <ExamAssignmentsTable
                    data={data}
                    setShowCreateEditModal={setShowCreateEditModal}
                    showSyncButton={isSuperAdmin}
                    setShowSyncModal={setShowSyncModal}
                    setShowDeleteModal={setShowDeleteModal}
                    setShowCompletionStatusReportModal={setShowCompletionStatusReportModal}
                    modalMode={modalMode}
                    setModalMode={setModalMode}
                    setSelectedExamAssignment={setSelectedExamAssignmentId}
                    searchString={searchString}
                />
            )}

            {showCreateEditModal && (
                <CreateEditExamAssignmentsModal
                    open={showCreateEditModal}
                    modalMode={modalMode}
                    resourceId={selectedExamAssignmentId}
                    handleClose={handleModalClose}
                />
            )}

            {showSyncModal && (
                <SyncAttemptsModal
                    open={showSyncModal}
                    assignmentId={selectedExamAssignmentId}
                    handleClose={handleModalClose}
                />
            )}

            {showDeleteModal && (
                <DeleteExamAssignmentModal
                    open={showDeleteModal}
                    resourceId={selectedExamAssignmentId}
                    handleClose={handleModalClose}
                />
            )}

            {showCompletionStatusReportModal &&
                selectedExamAssignmentId &&
                (() => {
                    const assignment = data.find((a) => a.id === selectedExamAssignmentId);
                    return (
                        <CompletionStatusReportModal
                            open={showCompletionStatusReportModal}
                            examAssignmentId={selectedExamAssignmentId}
                            examName={assignment?.examName ?? ''}
                            classIds={assignment?.classes?.map((c) => c.classId) ?? []}
                            onClose={() => {
                                setShowCompletionStatusReportModal(false);
                                setSelectedExamAssignmentId(null);
                            }}
                        />
                    );
                })()}
        </Container>
    );
};

export default ExamAssignmentsPage;
